import type { ConsentStatisticsRequest } from "../Generated/ConsentStatisticsRequest";

interface StatisticsResponse { ok: boolean; status: number; retryAfter: number | null; token?: string; }

export class ConsentStatisticsTransport {
    private token: Promise<string | null> | null = null;

    constructor(private readonly windowRef: Window) {}

    async send(report: ConsentStatisticsRequest): Promise<boolean> {
        try {
            const token = await (this.token ??= this.getToken());
            if (!token) { this.token = null; return false; }
            return (await this.request({
                method: "POST", headers: { "Content-Type": "application/json", RequestVerificationToken: token }, body: JSON.stringify(report)
            }))?.ok === true;
        } catch { /* Consent choices must work even when reporting is unavailable. */ }
        return false;
    }

    private async getToken(): Promise<string | null> {
        try {
            const response = await this.request({});
            if (!response?.ok) return null;
            return response.token ?? null;
        } catch { return null; }
    }

    private async request(options: RequestInit): Promise<StatisticsResponse | null> {
        let response = await this.requestOnce(options);
        if (response?.ok || response && response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429) return response;
        const delay = response?.status === 429 ? (response.retryAfter ?? 1) * 1000 + 50 : 250;
        if (delay > 5050) return response;
        await new Promise(resolve => this.windowRef.setTimeout(resolve, delay));
        return this.requestOnce(options);
    }

    private async requestOnce(options: RequestInit): Promise<StatisticsResponse | null> {
        const abort = new AbortController();
        const timer = this.windowRef.setTimeout(() => abort.abort(), 2000);
        try {
            const response = await this.windowRef.fetch("/consent/statistics", {
                credentials: "same-origin", cache: "no-store", keepalive: true, ...options, signal: abort.signal
            });
            const retryAfter = Number(response.headers?.get("Retry-After"));
            const result: StatisticsResponse = { ok: response.ok, status: response.status, retryAfter: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null };
            if (!options.method && response.ok) {
                const body = await response.json();
                if (typeof body?.token !== "string" || body.token.length === 0 || body.token.length > 2048) return { ...result, ok: false, status: 400 };
                result.token = body.token;
            }
            return result;
        } catch { return null; }
        finally { this.windowRef.clearTimeout(timer); }
    }
}
